var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isEqual } from "lodash";
import BigNumber from "bignumber.js";
import { findSubAccountById } from "@ledgerhq/coin-framework/account/index";
import { getTransactionData, getTypedTransaction } from "./transaction";
import { validateRecipient } from "./getTransactionStatus";
import { getNftCollectionMetadata } from "./api/nft";
import { getFeesEstimation, getGasEstimation, getTransactionCount } from "./api/rpc";
import { getAdditionalLayer2Fees, getEstimatedFees, isNftTransaction } from "./logic";
/**
 * Prepare basic coin transactions or smart contract interactions (other than live ERC20 transfers)
 * Should be used for transactions coming from the wallet API
 * Handling addition of gas limit
 */
export const prepareCoinTransaction = (account, typedTransaction) => __awaiter(void 0, void 0, void 0, function* () {
    // A `useAllAmount` transaction is a specific case of the live, and because we're in the
    // context of a coinTransaction, no smart contract should be involed
    if (typedTransaction.useAllAmount) {
        // Since a gas estimation is done by simulating the transaction, we can't know in advanced how much
        // we should put in the simulation.
        // But as a coin transaction (no smart contract) should always consumme the same amount of gas, no matter
        // the amount of coin transfered, we can infer the gasLimit with any amount.
        const gasLimit = yield getGasEstimation(account, Object.assign(Object.assign({}, typedTransaction), { amount: new BigNumber(0) }));
        const draftTransaction = Object.assign(Object.assign({}, typedTransaction), { gasLimit });
        const estimatedFees = getEstimatedFees(draftTransaction);
        const additionalFees = yield getAdditionalLayer2Fees(account.currency, draftTransaction);
        const amount = BigNumber.max(account.balance.minus(estimatedFees).minus(additionalFees || 0), 0);
        return Object.assign(Object.assign({}, draftTransaction), { amount,
            additionalFees });
    }
    const gasLimit = yield getGasEstimation(account, typedTransaction).catch(
    // in case of a smart contract interaction, the gas estimation
    // (which is transaction simulation by the node) can fail.
    // E.g. A DApp is creating an invalid transaction, swaping more Tokens than the user actually have -> fail
    // This value of 0 should be catched by `getTransactionStatus`
    // and displayed in the UI as `set the gas manually`
    () => new BigNumber(0));
    const additionalFees = yield getAdditionalLayer2Fees(account.currency, Object.assign(Object.assign({}, typedTransaction), { gasLimit }));
    return Object.assign(Object.assign({}, typedTransaction), { gasLimit,
        additionalFees });
});
/**
 * Prepare ERC20 transactions.
 * Handling addition of ERC20 transfer data and gas limit
 */
export const prepareTokenTransaction = (account, tokenAccount, typedTransaction) => __awaiter(void 0, void 0, void 0, function* () {
    const [recipientErrors] = validateRecipient(account, typedTransaction);
    const amount = typedTransaction.useAllAmount ? tokenAccount.balance : typedTransaction.amount;
    const data = !Object.keys(recipientErrors).length
        ? getTransactionData(account, Object.assign(Object.assign({}, typedTransaction), { amount }))
        : undefined;
    // As we're interacting with a smart contract,
    // it's going to be the real recipient for the tx
    const gasLimit = data
        ? yield getGasEstimation(account, Object.assign(Object.assign({}, typedTransaction), { amount: new BigNumber(0), recipient: tokenAccount.token.contractAddress, // recipient is then the token smart contract
            data })).catch(() => new BigNumber(0)) // this catch returning 0 should be handled by the `getTransactionStatus` method
        : new BigNumber(0);
    const additionalFees = yield getAdditionalLayer2Fees(account.currency, Object.assign(Object.assign({}, typedTransaction), { amount: new BigNumber(0), recipient: tokenAccount.token.contractAddress, // recipient is then the token smart contract
        data,
        gasLimit }));
    // Recipient isn't changed here as it would change on the UI end as well
    // The change will be handled by the `prepareForSignOperation` method
    // right before the device signature by the signOperation step
    return Object.assign(Object.assign({}, typedTransaction), { amount,
        data,
        gasLimit,
        additionalFees });
});
/**
 * Prepare ERC721/ERC1155 transactions.
 * Handling addition of NFT safeTransferFrom data and gas limit
 */
export const prepareNftTransaction = (account, typedTransaction) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const { currency } = account;
    const [recipientErrors] = validateRecipient(account, typedTransaction);
    const data = !Object.keys(recipientErrors).length
        ? getTransactionData(account, typedTransaction)
        : undefined;
    // As we're interacting with a smart contract,
    // it's going to be the real recipient for the tx
    const gasLimit = data
        ? yield getGasEstimation(account, Object.assign(Object.assign({}, typedTransaction), { amount: new BigNumber(0), recipient: typedTransaction.nft.contract, // recipient is then the nft smart contract
            data })).catch(() => new BigNumber(0)) // this catch returning 0 should be handled by the `getTransactionStatus` method
        : new BigNumber(0);
    const additionalFees = yield getAdditionalLayer2Fees(account.currency, Object.assign(Object.assign({}, typedTransaction), { amount: new BigNumber(0), recipient: typedTransaction.nft.contract, // recipient is then the token smart contract
        data,
        gasLimit }));
    // Providing the collection name to the transaction for the
    // deviceTransactionConfig step (so purely UI)
    const [collectionMetadata] = yield getNftCollectionMetadata([{ contract: typedTransaction.nft.contract }], ((_a = currency === null || currency === void 0 ? void 0 : currency.ethereumLikeInfo) === null || _a === void 0 ? void 0 : _a.chainId) || 0);
    const nft = Object.assign(Object.assign({}, typedTransaction.nft), { collectionName: ((_b = collectionMetadata.result) === null || _b === void 0 ? void 0 : _b.tokenName) || "" });
    // Recipient isn't changed here as it would change on the UI end as well
    // The change will be handled by the `prepareForSignOperation` method
    // right before the device signature by the signOperation step
    return Object.assign(Object.assign({}, typedTransaction), { data,
        gasLimit,
        additionalFees,
        nft });
});
/**
 * Method called to update a transaction into a state that would make it valid
 * (E.g. Adding fees, add smart contract data, etc...)
 */
export const prepareTransaction = (account, transaction) => __awaiter(void 0, void 0, void 0, function* () {
    const { currency } = account;
    // Get the current network status fees
    const feeData = yield (() => __awaiter(void 0, void 0, void 0, function* () {
        var _c, _d, _e, _f, _g, _h, _j;
        if (transaction.feesStrategy === "custom") {
            return {
                gasPrice: (_c = transaction.gasPrice) !== null && _c !== void 0 ? _c : null,
                maxFeePerGas: (_d = transaction.maxFeePerGas) !== null && _d !== void 0 ? _d : null,
                maxPriorityFeePerGas: (_e = transaction.maxPriorityFeePerGas) !== null && _e !== void 0 ? _e : null,
                nextBaseFee: (_h = (_g = (_f = transaction.gasOptions) === null || _f === void 0 ? void 0 : _f.medium) === null || _g === void 0 ? void 0 : _g.nextBaseFee) !== null && _h !== void 0 ? _h : null,
            };
        }
        if (!transaction.feesStrategy) {
            return getFeesEstimation(currency);
        }
        const gasOption = (_j = transaction.gasOptions) === null || _j === void 0 ? void 0 : _j[transaction.feesStrategy];
        return gasOption !== null && gasOption !== void 0 ? gasOption : getFeesEstimation(currency);
    }))();
    const subAccount = findSubAccountById(account, transaction.subAccountId || "");
    const typedTransaction = getTypedTransaction(transaction, feeData);
    const newTransaction = yield (() => {
        if (isNftTransaction(typedTransaction)) {
            return prepareNftTransaction(account, typedTransaction);
        }
        const isTokenTransaction = (subAccount === null || subAccount === void 0 ? void 0 : subAccount.type) === "TokenAccount";
        return isTokenTransaction
            ? prepareTokenTransaction(account, subAccount, typedTransaction)
            : prepareCoinTransaction(account, typedTransaction);
    })();
    // maintaining reference if the transaction hasn't change
    return isEqual(transaction, newTransaction) ? transaction : newTransaction;
});
/**
 * Prepare the transaction for the signOperation step.
 * For now, used to changed the recipient for TokenAccount transfers
 * with the smart contract address as recipient and add the nonce
 * (which would change as well in the UI if it was done before that step)
 */
export const prepareForSignOperation = (account, transaction) => __awaiter(void 0, void 0, void 0, function* () {
    var _k;
    const nonce = yield getTransactionCount(account.currency, account.freshAddress);
    if (isNftTransaction(transaction)) {
        return Object.assign(Object.assign({}, transaction), { amount: new BigNumber(0), recipient: transaction.nft.contract, // recipient is then the NFT smart contract
            // data as already been added by the `prepareTokenTransaction` method
            nonce });
    }
    const subAccount = findSubAccountById(account, (_k = transaction.subAccountId) !== null && _k !== void 0 ? _k : "");
    const isTokenTransaction = (subAccount === null || subAccount === void 0 ? void 0 : subAccount.type) === "TokenAccount";
    if (isTokenTransaction) {
        return Object.assign(Object.assign({}, transaction), { amount: new BigNumber(0), recipient: subAccount.token.contractAddress, // recipient is then the token smart contract
            // data as already been added by the `prepareTokenTransaction` method
            nonce });
    }
    return Object.assign(Object.assign({}, transaction), { nonce });
});
//# sourceMappingURL=prepareTransaction.js.map